import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings as saveSettingsUtil, PVQSettings, DEFAULT_SETTINGS } from '../utils/settings';
import { useRoadmapData } from '../hooks/useRoadmapData';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<PVQSettings>(getSettings());
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const { projects, loading: projectsLoading } = useRoadmapData();

  // Load settings from localStorage and fix invalid FAQ mode states
  useEffect(() => {
    const currentSettings = getSettings();
    
    // Check if we have an invalid state where both FAQ flags are true
    if (currentSettings.useKnowledgeBase && currentSettings.useAdvancedRAG) {
      console.log('Fixing invalid FAQ mode state: both flags were true, defaulting to Local RAG');
      const fixedSettings = {
        ...currentSettings,
        useKnowledgeBase: false,
        useAdvancedRAG: true
      };
      setSettings(fixedSettings);
      saveSettingsUtil(fixedSettings);
    } else {
      setSettings(currentSettings);
    }
  }, []);

  const handleSettingChange = (key: keyof PVQSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettingsAndNotify(newSettings);
  };

  const saveSettingsAndNotify = (newSettings: PVQSettings) => {
    setSaveStatus('saving');
    saveSettingsUtil(newSettings);
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('pvq-settings-changed'));
    
    // Simulate save delay for better UX
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 500);
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Settings</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>General Settings:</strong> Configure project selection and display preferences
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Project Selection */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Roadmap Project Selection
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Select which project's roadmap you want to view and edit across the application.
              </p>

              {projectsLoading ? (
                <div className="flex items-center text-gray-500">
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading projects...
                </div>
              ) : projects.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No projects found. Please create a project in the database first.
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="project-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Project
                  </label>
                  <select
                    id="project-select"
                    value={settings.selectedProjectId || ''}
                    onChange={(e) => handleSettingChange('selectedProjectId', e.target.value || null)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">-- Select a Project --</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} ({project.slug})
                      </option>
                    ))}
                  </select>

                  {/* Show selected project details */}
                  {settings.selectedProjectId && (() => {
                    const selectedProject = projects.find(p => p.id === settings.selectedProjectId);
                    return selectedProject ? (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-1">{selectedProject.name}</h4>
                        {selectedProject.description && (
                          <p className="text-sm text-blue-800 mb-2">{selectedProject.description}</p>
                        )}
                        <div className="text-xs text-blue-600">
                          Slug: <span className="font-mono">{selectedProject.slug}</span>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {!settings.selectedProjectId && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-yellow-900 mb-1">No Project Selected</h5>
                      <p className="text-sm text-yellow-800">
                        Please select a project from the dropdown above to access project-specific features like PI Alignment and System Architecture.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Show Project Form Sections */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Show Project Form Sections
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Display project-specific form sections in the sidebar navigation.
                    Each project can define custom form sections relevant to their workflow.
                  </p>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Project-specific sections like "PVQ Sections"
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settings.showPOCLinks}
                        onChange={(e) => handleSettingChange('showPOCLinks', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors duration-200 ${
                        settings.showPOCLinks ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-200 ${
                          settings.showPOCLinks ? 'transform translate-x-6' : ''
                        }`}></div>
                      </div>
                    </div>
                  </label>
                  <span className="text-xs text-gray-500 mt-1">
                    {settings.showPOCLinks ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Save Status */}
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                Settings are automatically saved to your browser's local storage.
              </div>
              <div className="flex items-center">
                {saveStatus === 'saving' && (
                  <div className="flex items-center text-blue-600">
                    <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className="flex items-center text-green-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Saved
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
            <button 
              onClick={() => {
                setSettings(DEFAULT_SETTINGS);
                saveSettingsAndNotify(DEFAULT_SETTINGS);
              }}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Reset to Defaults
            </button>
            <div className="space-x-3">
              <button className="px-6 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200">
                Export Settings
              </button>
              <button className="px-6 py-2 bg-gov-blue text-white rounded-md hover:bg-blue-800">
                Import Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;